<template>
    <q-page padding class="decodificador-page">

        <!-- Header -->
        <div class="page-header">
            <div class="row items-center no-wrap">
                <q-avatar size="56px" color="deep-purple" text-color="white">
                    <q-icon name="qr_code_scanner" size="32px" />
                </q-avatar>
                <div class="col q-ml-md">
                    <div class="text-h4 text-weight-bold text-grey-9">Decodificador de Productos</div>
                    <div class="text-subtitle2 text-grey-6">
                        Decodificá o generá códigos de producto a partir de sus características
                    </div>
                </div>
            </div>
        </div>

        <div class="row q-col-gutter-lg q-mt-lg">

            <!-- Input código -->
            <div class="col-12">
                <q-card class="input-card">
                    <q-card-section class="card-header-decode">
                        <div class="row items-center no-wrap">
                            <q-icon name="search" size="28px" class="q-mr-sm" />
                            <div class="col">
                                <div class="text-h6 text-weight-bold">Ingresar Código</div>
                                <div class="text-caption text-white-7">Ingresá el código de 15 caracteres del producto</div>
                            </div>
                        </div>
                    </q-card-section>
                    <q-separator />
                    <q-card-section class="q-pa-lg">
                        <q-input
                            v-model="codigoInput"
                            outlined
                            label="Código del producto"
                            placeholder="Ej: MRCC5ZPB5T40I05"
                            maxlength="15"
                            clearable
                            @keyup.enter="decodificar"
                            :error="!!decodeError"
                            :error-message="decodeError"
                        >
                            <template v-slot:prepend>
                                <q-icon name="qr_code" color="deep-purple" />
                            </template>
                            <template v-slot:append>
                                <q-badge
                                    v-if="codigoInput"
                                    :color="codigoInput.length === 15 ? 'positive' : 'warning'"
                                    class="q-mr-sm"
                                >
                                    {{ codigoInput.length }}/15
                                </q-badge>
                                <q-btn
                                    unelevated
                                    color="deep-purple"
                                    icon="search"
                                    label="Decodificar"
                                    @click="decodificar"
                                    :loading="loadingDecode"
                                    :disable="!codigoInput || codigoInput.length !== 15"
                                    no-caps
                                    dense
                                    class="decode-btn"
                                >
                                    <template v-slot:loading>
                                        <q-spinner-hourglass size="18px" />
                                    </template>
                                </q-btn>
                            </template>
                        </q-input>

                        <!-- Visual del código -->
                        <div class="codigo-visual q-mt-md">
                            <div class="text-caption text-grey-6 q-mb-xs">Vista del código:</div>
                            <div class="codigo-chars row no-wrap">
                                <div
                                    v-for="(char, idx) in codigoDisplayPadded"
                                    :key="idx"
                                    class="codigo-char"
                                    :class="charSegmentClass(idx)"
                                >
                                    <span class="char-value">{{ char || '·' }}</span>
                                    <span class="char-index">{{ idx + 1 }}</span>
                                </div>
                            </div>
                            <div class="seg-labels-row q-mt-xs">
                                <div
                                    v-for="seg in posicionesLegend"
                                    :key="seg.type"
                                    class="seg-label-item"
                                    :style="{ '--seg-cant': seg.cant }"
                                >
                                    <div class="seg-label-chip" :style="{ background: segColors[seg.color] }">
                                        {{ seg.label }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </q-card-section>
                </q-card>
            </div>

            <!-- Características (siempre visible) -->
            <div class="col-12">
                <q-card class="result-card">
                    <q-card-section class="card-header-result">
                        <div class="row items-center no-wrap">
                            <q-icon name="tune" size="28px" class="q-mr-sm" />
                            <div class="col">
                                <div class="text-h6 text-weight-bold">
                                    {{ descripcionDecodificada || 'Decodificá un código o seleccioná las características' }}
                                </div>
                            </div>
                            <q-btn v-if="lastDecodedCodigo" flat round dense icon="content_copy" color="white" @click="copiarTexto(lastDecodedCodigo, 'Código')">
                                <q-tooltip>Copiar código</q-tooltip>
                            </q-btn>
                            <q-btn v-if="descripcionDecodificada" flat round dense icon="description" color="white" @click="copiarTexto(descripcionDecodificada, 'Descripción')">
                                <q-tooltip>Copiar descripción</q-tooltip>
                            </q-btn>
                            <q-btn v-if="lastDecodedCodigo" flat round dense icon="restart_alt" color="white" @click="limpiarTodo">
                                <q-tooltip>Limpiar todo</q-tooltip>
                            </q-btn>
                        </div>
                    </q-card-section>
                    <q-separator />
                    <q-card-section class="q-pa-md">
                        <q-inner-loading :showing="loadingVars">
                            <q-spinner-hourglass size="50px" color="deep-purple" />
                        </q-inner-loading>
                        <div v-if="!loadingVars" class="row q-col-gutter-md">
                            <div
                                v-for="pos in posiciones"
                                :key="pos.type"
                                class="col-12 col-sm-6 col-md-4"
                            >
                                <div class="result-item">
                                    <div class="result-icon">
                                        <q-icon :name="getCaractIcon(pos.label)" size="20px" color="deep-purple" />
                                    </div>
                                    <div class="result-content">
                                        <div class="result-label">{{ pos.label }}</div>
                                        <q-select
                                            :model-value="form[pos.type]"
                                            @update:model-value="val => form[pos.type] = val"
                                            :options="variables[pos.caract]"
                                            option-label="label"
                                            option-value="value"
                                            emit-value map-options
                                            borderless dense clearable
                                            class="result-select"
                                        >
                                            <template v-slot:selected-item="scope">
                                                <div class="row items-center no-wrap" style="gap:6px">
                                                    <color-dot v-if="pos.caract === 'color'" :label="scope.opt.label" />
                                                    <span class="result-select-value">{{ scope.opt.label }}</span>
                                                </div>
                                            </template>
                                            <template v-slot:option="scope">
                                                <q-item v-bind="scope.itemProps">
                                                    <q-item-section v-if="pos.caract === 'color'" avatar style="min-width:28px">
                                                        <color-dot :label="scope.opt.label" />
                                                    </q-item-section>
                                                    <q-item-section>{{ scope.opt.label }}</q-item-section>
                                                </q-item>
                                            </template>
                                        </q-select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </q-card-section>
                </q-card>
            </div>

        </div>
    </q-page>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineComponent, h } from 'vue';
import { useQuasar } from 'quasar';
import CodigoService from '@/services/CodigoService.js';

const $q = useQuasar();

const loadingVars = ref(false);
const loadingDecode = ref(false);
const decodeError = ref('');
const codigoInput = ref('');
const decodeResult = ref([]);
const lastDecodedCodigo = ref('');

const variables = ref({
    tipo: [], nucleo: [], espesorPanel: [], densidad: [],
    revestimiento: [], color: [], espesorCara: [], terminacion: [],
});

// Posiciones vienen del backend (CodigoConfigurationProvider) — única fuente de verdad
const posiciones = ref([]);

const form = ref({
    tipo: null, nucleo: null, espesor: null, densidad: null,
    extRev: null, extCol: null, extEsp: null, extTerm: null,
    intRev: null, intCol: null, intEsp: null, intTerm: null,
});

// ── Colores de segmento ──────────────────────────────────────
const segColors = {
    'deep-purple': '#4527a0',
    'orange-8':    '#e65100',
    'teal-7':      '#00695c',
    'blue-grey-7': '#455a64',
    'brown-7':     '#4e342e',
    'indigo-7':    '#283593',
};

const posicionesLegend = [
    { type: 'tipo',     label: 'Tipo',     color: 'deep-purple', cant: 2 },
    { type: 'exterior', label: 'Exterior', color: 'orange-8',    cant: 4 },
    { type: 'interior', label: 'Interior', color: 'teal-7',      cant: 4 },
    { type: 'densidad', label: 'Densidad', color: 'blue-grey-7', cant: 2 },
    { type: 'nucleo',   label: 'Núcleo',   color: 'brown-7',     cant: 1 },
    { type: 'espesor',  label: 'Espesor',  color: 'indigo-7',    cant: 2 },
];

const segmentByIndex = [
    'deep-purple','deep-purple',
    'orange-8','orange-8','orange-8','orange-8',
    'teal-7','teal-7','teal-7','teal-7',
    'blue-grey-7','blue-grey-7',
    'brown-7',
    'indigo-7','indigo-7',
];

const charSegmentClass = (idx) => `seg-${segmentByIndex[idx].replace('-','_')}`;

const codigoInputPadded = computed(() => {
    const arr = (codigoInput.value || '').split('');
    while (arr.length < 15) arr.push('');
    return arr.slice(0, 15);
});

// La visual muestra el código generado por los selects en tiempo real
const codigoDisplayPadded = computed(() => {
    const src = generatedCode.value || codigoInput.value || '';
    const arr = src.split('').map(c => c === '·' ? '' : c);
    while (arr.length < 15) arr.push('');
    return arr.slice(0, 15);
});

// ── Decodificar ──────────────────────────────────────────────
const getCaractLabel = (item) => item.split(':')[0]?.trim() ?? item;
const getCaractValue = (item) => item.split(':').slice(1).join(':').trim();

const getCaractIcon = (label) => {
    const l = label.toLowerCase();
    if (l.includes('tipo')) return 'category';
    if (l.includes('color')) return 'palette';
    if (l.includes('revestimiento')) return 'layers';
    if (l.includes('espesor') && l.includes('panel')) return 'straighten';
    if (l.includes('espesor')) return 'straighten';
    if (l.includes('terminac')) return 'format_paint';
    if (l.includes('densidad')) return 'blur_on';
    if (l.includes('cleo')) return 'layers';
    return 'info';
};

const decodificar = async () => {
    if (!codigoInput.value || codigoInput.value.length !== 15) return;
    decodeError.value = '';
    loadingDecode.value = true;
    try {
        const result = await CodigoService.decodificar(codigoInput.value);
        decodeResult.value = Array.isArray(result) ? result : [];
        lastDecodedCodigo.value = codigoInput.value;

        if (!decodeResult.value.length) {
            decodeError.value = 'No se encontraron características para este código';
            return;
        }

        // Reverse-populate selectores usando posiciones del backend como única fuente
        Object.keys(form.value).forEach(k => { form.value[k] = null; });
        for (const item of decodeResult.value) {
            const decodedLabel = getCaractLabel(item);
            const decodedValue = getCaractValue(item);
            const pos = posiciones.value.find(p => p.label === decodedLabel);
            if (!pos) continue;
            const option = variables.value[pos.caract]?.find(v => v.label === decodedValue);
            if (option) form.value[pos.type] = option.value;
        }
    } catch {
        decodeError.value = 'Error al decodificar el código';
        $q.notify({ type: 'negative', message: 'Error al decodificar el código', icon: 'error' });
    } finally {
        loadingDecode.value = false;
    }
};

// ── Código generado desde selectores ────────────────────────
const generatedCode = computed(() => {
    if (!posiciones.value.length) return '';
    const code = new Array(15).fill('·');
    let any = false;
    for (const pos of posiciones.value) {
        const val = form.value[pos.type];
        if (val !== null && val !== undefined && val !== '') {
            any = true;
            const strVal = String(val);
            for (let i = 0; i < pos.cant; i++) code[pos.desde + i] = strVal[i] ?? '·';
        }
    }
    return any ? code.join('') : '';
});

// Sincroniza codigoInput con cualquier cambio en los selects
watch(generatedCode, (val) => {
    if (val) {
        codigoInput.value = val.replace(/·/g, '');
    }
});

const getLabel = (formKey, caract) => {
    const val = form.value[formKey];
    if (!val) return '';
    return variables.value[caract]?.find(v => v.value === val)?.label ?? '';
};

const descripcionDecodificada = computed(() => {
    const parts = [];

    parts.push('PANEL');

    const tipo = getLabel('tipo', 'tipo');
    if (tipo) parts.push(tipo);

    const ext = [form.value.extRev, form.value.extCol, form.value.extEsp, form.value.extTerm].map(v => v ?? '').join('');
    const int_ = [form.value.intRev, form.value.intCol, form.value.intEsp, form.value.intTerm].map(v => v ?? '').join('');
    if (ext || int_) parts.push(`${ext}/${int_}`);

    const nucleo = getLabel('nucleo', 'nucleo');
    if (nucleo) parts.push(nucleo);

    const espesor = getLabel('espesor', 'espesorPanel');
    if (espesor) parts.push(espesor);

    return parts.join(' ');
});

const limpiarTodo = () => {
    Object.keys(form.value).forEach(k => { form.value[k] = null; });
    codigoInput.value = '';
    decodeResult.value = [];
    lastDecodedCodigo.value = '';
    decodeError.value = '';
};

const copiarTexto = (texto, tipo = 'Texto') => {
    if (!texto) return;
    navigator.clipboard.writeText(texto).then(() => {
        $q.notify({ type: 'positive', message: `${tipo} "${texto}" copiado`, icon: 'content_copy' });
    });
};

// ── ColorDot inline component ────────────────────────────────
const colorKeywords = {
    'blanco': '#f0f0f0', 'negro': '#212121', 'gris': '#9e9e9e', 'plata': '#bdbdbd',
    'azul': '#1565c0', 'rojo': '#c62828', 'verde': '#2e7d32', 'amarillo': '#f9a825',
    'naranja': '#e65100', 'marrón': '#5d4037', 'marron': '#5d4037',
    'beige': '#d7ccc8', 'crema': '#fff9c4', 'corten': '#bf6b2a',
    'inox': '#e0e0e0', 'galvanizado': '#bdbdbd', 'peugeot': '#1a3a6e',
    'milenium': '#2c4a8e', 'colonial': '#8b3a1a',
};

const ColorDot = defineComponent({
    name: 'ColorDot',
    props: { label: String },
    setup(props) {
        const bg = computed(() => {
            if (!props.label) return '#bdbdbd';
            const lower = props.label.toLowerCase();
            const key = Object.keys(colorKeywords).find(k => lower.includes(k));
            return key ? colorKeywords[key] : '#bdbdbd';
        });
        return () => h('div', {
            style: {
                width: '16px', height: '16px', borderRadius: '50%',
                border: '1px solid rgba(0,0,0,0.15)', flexShrink: '0',
                background: bg.value,
            },
        });
    },
});

onMounted(async () => {
    loadingVars.value = true;
    try {
        [variables.value, posiciones.value] = await Promise.all([
            CodigoService.variables(),
            CodigoService.posiciones(),
        ]);
    } catch {
        $q.notify({ type: 'negative', message: 'Error al cargar las variables', icon: 'error' });
    } finally {
        loadingVars.value = false;
    }
});
</script>

<style lang="scss" scoped>
.decodificador-page {
    background: linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%);
    min-height: 100vh;
}

// ── Header ────────────────────────────────────────────────────
.page-header {
    background: white;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 4px 20px rgba(0,0,0,.08);
    animation: fadeInDown .5s ease;
    &:hover { box-shadow: 0 8px 30px rgba(0,0,0,.12); }
}

// ── Input card ───────────────────────────────────────────────
.input-card, .result-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0,0,0,.08);
    transition: box-shadow .3s ease;
    &:hover { box-shadow: 0 8px 30px rgba(0,0,0,.12); }
}

.card-header-decode {
    background: linear-gradient(135deg, #4527a0 0%, #311b92 100%);
    color: white;
    padding: 24px;
}

.card-header-result {
    background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
    color: white;
    padding: 24px;
}

.text-white-7 { color: rgba(255,255,255,.85); }

.decode-btn {
    border-radius: 6px;
    padding: 0 14px;
    height: 36px;
}

// Visual código
.codigo-visual {
    background: #f8f5ff;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #e8e0ff;
}

$char-w: 42px;
$char-gap: 3px;

.codigo-chars { gap: $char-gap; overflow-x: auto; }

.codigo-char {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: $char-w;
    min-width: $char-w;
    flex-shrink: 0;
    height: 54px;
    border-radius: 6px;
    padding: 4px 2px;
    background: white;
    border: 2px solid #e0e0e0;

    .char-value { font-family: 'Courier New', monospace; font-size: 18px; font-weight: 700; line-height: 1.2; }
    .char-index { font-size: 9px; color: #9e9e9e; }

    &.seg-deep_purple  { border-color: #7c4dff; background: #f3e5f5; .char-value { color: #4527a0; } }
    &.seg-orange_8     { border-color: #ff6d00; background: #fff3e0; .char-value { color: #e65100; } }
    &.seg-teal_7       { border-color: #009688; background: #e0f2f1; .char-value { color: #00695c; } }
    &.seg-blue_grey_7  { border-color: #607d8b; background: #eceff1; .char-value { color: #455a64; } }
    &.seg-brown_7      { border-color: #795548; background: #efebe9; .char-value { color: #4e342e; } }
    &.seg-indigo_7     { border-color: #3f51b5; background: #e8eaf6; .char-value { color: #283593; } }
}

.seg-labels-row {
    display: flex;
    flex-wrap: nowrap;
    gap: $char-gap;
}

.seg-label-item {
    width: calc(var(--seg-cant) * (#{$char-w} + #{$char-gap}) - #{$char-gap});
    flex-shrink: 0;
}

.seg-label-chip {
    height: 22px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    color: white;
    letter-spacing: .3px;
    white-space: nowrap;
    overflow: hidden;
}

// ── Result items con select embebido ─────────────────────────
.result-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    background: #f8f5ff;
    border-radius: 12px;
    border-left: 4px solid #7c4dff;
    height: 100%;
    transition: background .2s ease;
    &:hover { background: #ede7f6; }
}

.result-icon { padding-top: 4px; flex-shrink: 0; }

.result-content {
    flex: 1;
    min-width: 0;
}

.result-label {
    font-size: 10px;
    color: #7e57c2;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .5px;
    margin-bottom: 2px;
}

.result-select {
    :deep(.q-field__control) { padding: 0; min-height: 28px; }
    :deep(.q-field__native)  { font-size: 14px; font-weight: 600; color: #212121; padding: 2px 0; }
    :deep(.q-field__marginal) { color: #9e9e9e; height: 28px; }
    :deep(.q-select__dropdown-icon) { color: #9e9e9e; font-size: 18px; }
}

.result-select-value {
    font-size: 14px;
    font-weight: 600;
    color: #212121;
}

// ── Animations ────────────────────────────────────────────────
@keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-30px); }
    to   { opacity: 1; transform: translateY(0); }
}

@media (max-width: 1023px) {
    .page-header { border-radius: 12px; padding: 20px; }
    .codigo-char { min-width: 26px; .char-value { font-size: 14px; } }
}
</style>
